'use client'

import { useEffect, useRef, useState } from 'react'
import { Button, Dialog, Navigation, Text, Container, Icon, Spacer, Cell } from '@vinted/web-ui'
import { ArrowLeft24, InfoCircle16, Redirect16, X24 } from '@vinted/monochrome-icons'

import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { redirectToPageInNewTab } from '@marketplace-web/browser/browser-navigation-util'
import { ClickAdEventTarget, VanPlacementConfigModel } from '@marketplace-web/ads/ads-data'

import AdInfo from '../../AdInfo'
import useAdEventTracking from '../../../../hooks/useAdEventTracking'
import useFetchVanCreative from '../../../../hooks/useFetchVanCreative'
import useClickAdEventTracking from '../../../../hooks/useClickAdEventTracking'
import { VAN_CAMPAIGN_PRIVACY_POLICY_URL, PRIVACY_POLICY_URL } from '../../../../constants/routes'
import DotsIndicator from './DotsIndicator'

type Props = {
  isOpen: boolean
  placements: Array<VanPlacementConfigModel>
  onClose: (target: ClickAdEventTarget) => void
  onNavigateBack?: (target: ClickAdEventTarget) => void
  currentPlacementIndex: number
  onPlacementIndexChange: (index: number) => void
}

const MAX_DIALOG_WIDTH = '375px'

const VanMultiOfferContent = ({
  isOpen,
  placements,
  onClose,
  onNavigateBack,
  currentPlacementIndex,
  onPlacementIndexChange,
}: Props) => {
  const [isAdRendered, setIsAdRendered] = useState(false)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const trackedReceivedAdIds = useRef<Set<string>>(new Set())
  const trackedViewAdIds = useRef<Set<string>>(new Set())

  const currentPlacement = placements[currentPlacementIndex]
  const creative = currentPlacement?.creative
  const placementId = currentPlacement?.id

  const shouldTrackReceivedAd = placementId ? !trackedReceivedAdIds.current.has(placementId) : false
  const shouldTrackViewAd = placementId ? !trackedViewAdIds.current.has(placementId) : false

  const handleReceivedAdTracked = () => {
    if (!placementId) return

    trackedReceivedAdIds.current.add(placementId)
  }

  const handleViewAdTracked = () => {
    if (!placementId) return

    trackedViewAdIds.current.add(placementId)
  }

  const { viewableImpressionRef, impressionRef } = useAdEventTracking({
    isAdRendered,
    placementConfig: currentPlacement || ({} as VanPlacementConfigModel),
    customPlacementId: currentPlacement?.placementId,
    shouldTrackReceivedAd,
    shouldTrackViewAd,
    onReceivedAdTracked: handleReceivedAdTracked,
    onViewAdTracked: handleViewAdTracked,
  })
  const { trackClickAdEvent } = useClickAdEventTracking()

  const translate = useTranslate('vinted_ads_network.ad_offer')
  const translateMulti = useTranslate('vinted_ads_network.multiple_ad_offer')

  const { iframeContent } = useFetchVanCreative({
    config: currentPlacement || ({} as VanPlacementConfigModel),
    creative: creative || ({} as NonNullable<VanPlacementConfigModel['creative']>),
  })

  useEffect(() => {
    if (!iframeContent || !isOpen) return

    setIsAdRendered(true)
  }, [iframeContent, isOpen])

  const onOfferButtonClick = () => {
    if (!creative) return

    redirectToPageInNewTab(creative.clickThroughUrl)
    trackClickAdEvent(currentPlacement, ClickAdEventTarget.Cta)
  }

  const onAdInfoClick = () => {
    if (!currentPlacement?.id) return

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.AdInfo)

    redirectToPageInNewTab(VAN_CAMPAIGN_PRIVACY_POLICY_URL(currentPlacement.id))
  }

  const onPrivacyPolicyClick = () => {
    if (!currentPlacement) return

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.PrivacyPolicy)

    redirectToPageInNewTab(PRIVACY_POLICY_URL)
  }

  const onNextOffer = () => {
    if (!currentPlacement) return

    const nextIndex = currentPlacementIndex >= placements.length - 1 ? 0 : currentPlacementIndex + 1

    trackClickAdEvent(currentPlacement, ClickAdEventTarget.SecondaryButton)
    setIsAdRendered(false)
    onPlacementIndexChange(nextIndex)
  }

  if (!currentPlacement || !creative) return null

  if (!iframeContent || !isOpen || !currentPlacement.id) return null

  const offerCount = placements.length
  const isMultipleOffers = offerCount > 1
  const subtitleKey = isMultipleOffers ? 'multiple' : 'single'

  return (
    <Dialog
      contentDimensions={{
        maxWidth: MAX_DIALOG_WIDTH,
      }}
      show={isOpen}
      closeOnOverlay
      defaultCallback={() => onClose(ClickAdEventTarget.CloseClickOutsidePlacement)}
      testId="van-multi-offer-content-dialog"
    >
      <div ref={viewableImpressionRef} className="van-multi-offer-content-wrapper">
        <div ref={impressionRef} key={placementId || currentPlacementIndex}>
          <Navigation
            body={translate('header_title')}
            left={
              onNavigateBack && (
                <Button
                  inline
                  iconName={ArrowLeft24}
                  iconColor="greyscale-level-2"
                  styling="flat"
                  testId="van-offer-navigation-back"
                  aria={{ 'aria-label': translate('a11y.actions.back') }}
                  onClick={() => onNavigateBack(ClickAdEventTarget.BackNavigationButton)}
                />
              )
            }
            right={
              <Button
                inline
                iconName={X24}
                iconColor="greyscale-level-2"
                styling="flat"
                testId="van-offer-navigation-close"
                aria={{ 'aria-label': translate('a11y.actions.close') }}
                onClick={() => onClose(ClickAdEventTarget.CloseButton)}
              />
            }
          />
          <Spacer size="large" />
          <Container paddingLeft="large" paddingRight="large">
            <div className="van-offer-container">
              <div data-testid="van-offer-slot" id="van-offer-slot">
                <iframe
                  ref={iframeRef}
                  id="van-offer-iframe"
                  title="van-offer-iframe"
                  width={creative.size.width}
                  height={creative.size.height}
                  className="van-offer-iframe"
                  srcDoc={iframeContent}
                />
              </div>
              <Spacer size="x-large" />
              <div style={{ width: creative.size.width }}>
                <Text
                  as="h2"
                  type="section-heading"
                  text={translateMulti('title', { count: offerCount }, { count: offerCount })}
                  alignment="center"
                  width="parent"
                  testId="van-offer-title"
                />
                <Spacer size="small" />
                <Text
                  as="p"
                  text={translateMulti(`subtitle.${subtitleKey}`)}
                  type="subtitle"
                  alignment="center"
                  width="parent"
                  testId="van-offer-subtitle"
                />
                <Spacer size="x-large" />
                <Button
                  text={translate('button')}
                  iconPosition="right"
                  iconName={Redirect16}
                  styling="filled"
                  size="medium"
                  theme="primary"
                  onClick={onOfferButtonClick}
                  testId="van-offer-claim-button"
                />
                {isMultipleOffers && (
                  <>
                    <Spacer size="medium" />
                    <Button
                      text={translateMulti('button.secondary')}
                      iconPosition="right"
                      styling="outlined"
                      size="medium"
                      theme="primary"
                      onClick={onNextOffer}
                      testId="van-offer-secondary-button"
                    />
                  </>
                )}

                <Spacer size="x-large" />
                <div className="van-multi-offer-info-container">
                  <div>
                    <Cell
                      styling="tight"
                      clickable
                      body={
                        <div className="van-multi-offer-info">
                          <Icon name={InfoCircle16} color="greyscale-level-2" />
                          <AdInfo underline />
                        </div>
                      }
                      onClick={onAdInfoClick}
                      testId="ad-info-redirection-button"
                    />
                  </div>
                  {isMultipleOffers && (
                    <DotsIndicator
                      currentIndex={currentPlacementIndex}
                      totalItems={placements.length}
                    />
                  )}
                  <div>
                    <Cell
                      styling="tight"
                      clickable
                      body={
                        <div className="van-multi-offer-info-text">
                          <Text
                            as="p"
                            type="caption"
                            theme="muted"
                            text={translate('privacy_policy')}
                            underline
                            alignment="right"
                          />
                        </div>
                      }
                      onClick={onPrivacyPolicyClick}
                      testId="privacy-policy-redirection-button"
                    />
                  </div>
                </div>
                <Spacer size="x-large" />
              </div>
            </div>
          </Container>
        </div>
      </div>
    </Dialog>
  )
}

export default VanMultiOfferContent
